import { Box } from '@mentimeter/ragnar-ui/box';
import { postCreateRequest } from '@core-api/participant-rating/participant-rating/create/{vote_key}';
import { SendingIllustration } from '@mentimeter/ragnar-visuals/spotlight';
import React, { useState } from 'react';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Button, Form, Textarea } from '@mentimeter/ragnar-form';
import { IconButton } from '@mentimeter/ragnar-ui/icon-button';
import { StarIcon, StarSolidIcon } from '@mentimeter/ragnar-visuals';
import type { Regions, TrackVoter } from '@mentimeter/http-clients';
import { BottomPanel } from '@mentimeter/voting-ui';
import { useRagnar } from '@mentimeter/ragnar-react';
import { getRegionByVoteKey } from '@mentimeter/region';
import { useTranslations } from 'next-intl';
import shuffle from 'lodash/shuffle';
import type { BeesActionableFeedbackVersion } from './BeesActionableFeedbackExperiment';

type RatingValue = 1 | 2 | 3 | 4 | 5;
interface Rating {
  id: number;
  value: RatingValue;
  label: string;
}

const starRatings: Rating[] = [
  { id: 1, value: 1, label: 'Hard to stay focused' },
  { id: 2, value: 2, label: 'Some sparks here and there' },
  { id: 3, value: 3, label: 'Pretty decent' },
  { id: 4, value: 4, label: 'Nicely done!' },
  { id: 5, value: 5, label: 'Spot on!' },
];

const IMPROVEMENT_OPTIONS = [
  'More interaction',
  'Clearer purpose',
  'More variety',
  'More examples',
  'Deeper questions',
  'Faster tempo',
  'Clearer structure',
];

const StructuredFeedbackOptions = ({
  selectedRatingValue,
  feedback,
  setFeedback,
  randomize,
  setPosition,
}: {
  selectedRatingValue: number;
  feedback: string;
  setFeedback: (message: string) => void;
  randomize: boolean;
  setPosition: (position: number | null) => void;
}) => {
  const [options, _] = useState(
    randomize ? shuffle(IMPROVEMENT_OPTIONS) : IMPROVEMENT_OPTIONS,
  );
  return (
    <Box
      flexDirection="row"
      width="75%"
      mt="space2"
      mb="space8"
      flexWrap="wrap"
      justifyContent="center"
    >
      {options.map((option, index) => (
        <Button
          m="space1"
          key={option}
          onClick={() => {
            setPosition(index + 1);
            return feedback === option ? setFeedback('') : setFeedback(option);
          }}
          variant={feedback === option ? 'primary' : 'tertiary'}
          type="button"
        >
          {option}
        </Button>
      ))}
    </Box>
  );
};

const UnstructuredFeedbackInput = ({
  selectedRatingValue,
  feedback,
  setFeedback,
}: {
  selectedRatingValue: number;
  feedback: string;
  setFeedback: (message: string) => void;
}) => {
  return (
    <Box width="120%" mt="space2" mb="space8">
      <Textarea
        label=""
        name="message"
        placeholder={
          selectedRatingValue === 5
            ? 'I appreciated that...'
            : 'I think the session could improve by...'
        }
        minHeight="120px"
        maxLength={300}
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
      />
    </Box>
  );
};

export const BeesActionableFeedback = ({
  voteKey,
  version,
  trackVoter,
}: {
  voteKey: string;
  version: BeesActionableFeedbackVersion;
  trackVoter: (data: Parameters<TrackVoter>[0], region?: Regions) => any;
}) => {
  const [feedback, setFeedback] = React.useState('');
  const [position, setPosition] = React.useState<number | null>(null);
  const [submittedRating, setSubmittedRating] = React.useState(false);
  const [selectedRating, setSelectedRating] = React.useState({
    id: 0,
    value: 0,
    label: 'How engaging was this Menti?',
  });
  const [show, setShow] = React.useState(true);
  const t = useTranslations('participating.bees_actionable_feedback');

  const handleClose = React.useCallback(() => {
    setShow(false);
  }, [setShow]);
  const { theme } = useRagnar();
  const region = getRegionByVoteKey(voteKey);

  return (
    <BottomPanel show={show} onDismiss={handleClose} themeBg={theme.colors.bg}>
      <Box
        alignItems="center"
        justifyContent="center"
        width="100%"
        py="space14"
        px="space4"
      >
        <Box alignItems="center">
          {!submittedRating ? (
            <>
              <Text fontSize="125">{selectedRating.label}</Text>
              <Form
                initialValues={{ rating: null }}
                onSubmit={async () => {
                  await fetch(
                    postCreateRequest({
                      region,
                      voteKey,
                      rating: selectedRating.value as RatingValue,
                      feedback,
                      version,
                      ...(position && { position }),
                    }),
                  );

                  trackVoter({
                    event: 'Clicked submit feedback',
                    properties: {
                      rating: selectedRating.value,
                      version,
                    },
                  });
                  setSubmittedRating(true);
                  if (handleClose) {
                    setTimeout(handleClose, 2000);
                  }
                }}
              >
                <Box
                  mb="space6"
                  mt="space4"
                  flexDirection="row"
                  gap="space3"
                  justifyContent="center"
                  width="100%"
                >
                  {starRatings.map((rating, index) => {
                    const inSelectedRange =
                      selectedRating && index < selectedRating.value;
                    return (
                      <IconButton
                        key={rating.id}
                        variant="tertiary"
                        type="button"
                        onClick={() => {
                          trackVoter({
                            event: 'Clicked star',
                            properties: {
                              rating: rating.value,
                              version,
                            },
                          });
                          setSelectedRating(rating);
                        }}
                      >
                        {inSelectedRange ? <StarSolidIcon /> : <StarIcon />}
                      </IconButton>
                    );
                  })}
                </Box>
                {selectedRating.value !== 0 && (
                  <Box width="100%" alignItems="center">
                    {version === 'stars_only' ? (
                      <Text mb="space4" color="textWeaker">
                        {t('rate_the_engagement')}
                      </Text>
                    ) : (
                      <Box maxWidth="250px">
                        <Text textAlign="center">
                          {selectedRating.value === 5
                            ? t('appreciated_feedback')
                            : t('constructive_feedback')}
                        </Text>
                      </Box>
                    )}
                    {version === 'stars_and_unstructured_feedback' && (
                      <UnstructuredFeedbackInput
                        feedback={feedback}
                        setFeedback={setFeedback}
                        selectedRatingValue={selectedRating.value}
                      />
                    )}
                    {version === 'stars_and_structured_feedback' && (
                      <StructuredFeedbackOptions
                        feedback={feedback}
                        setFeedback={setFeedback}
                        selectedRatingValue={selectedRating.value}
                        randomize={false}
                        setPosition={setPosition}
                      />
                    )}
                    {version === 'stars_and_random_feedback' && (
                      <StructuredFeedbackOptions
                        feedback={feedback}
                        setFeedback={setFeedback}
                        selectedRatingValue={selectedRating.value}
                        randomize={true}
                        setPosition={setPosition}
                      />
                    )}
                    <Button variant="primary" type="submit">
                      {t('submit_feedback_button')}
                    </Button>
                  </Box>
                )}
              </Form>
            </>
          ) : (
            <Box alignItems="center" width="100%" mb="space6">
              <Box width="100px" my="space4">
                <SendingIllustration />
              </Box>
              <Text color="text" textAlign="center">
                {t('thank_you_for_feedback')}
              </Text>
              <Text color="text" textAlign="center">
                {t('feedback_received')}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    </BottomPanel>
  );
};
